// Alias cloud-sync driver (docs/08 §8.3), used verbatim by BOTH hosts: the web
// bootstrap (shell/services.ts) and the headless brain (runtime/main.ts), so
// iOS (brain) and Android (Capacitor WebView) inherit sync with no native work.
//
// It sits ON TOP of the §2 device-local alias stores (which stay the rendering
// source and the offline store) and is invisible to UI code. The whole map is
// the sync unit: last-write-wins, no tombstones (docs/08 §8.3).
//
// PRIVACY: only the opaque ciphertext blob (identity/aliasBlob.ts) ever leaves
// the device. This module never logs an alias, a map, a blob, or the key.

import type { AliasesBlobResponse, PutAliasesBlobResponse } from '@merkury/protocol';
import { fromBase64, toBase64 } from '../lib/bytes.js';
import { decryptAliases, encryptAliases } from '../identity/aliasBlob.js';

/** The two REST calls the driver needs — satisfied by RestClient on both hosts. */
export interface AliasSyncRest {
  getAliasesBlob(): Promise<AliasesBlobResponse>;
  putAliasesBlob(blob: string): Promise<PutAliasesBlobResponse>;
}

/**
 * The device-local alias store seam. `getMap`/`replaceMap` bridge to the host's
 * store (web localStorage store, brain KvAliases); `subscribe` fires on any
 * local mutation (web: onAliasesChanged; the brain leaves it a no-op and calls
 * `notifyLocalChange` from its own mutation points instead). `replaceMap` MAY be
 * async and MUST wholesale-replace, then do any host-specific re-priming (the
 * brain re-emits contacts + re-primes the effective-name caches inside it).
 */
export interface AliasSyncStore {
  getMap(): Record<string, string>;
  replaceMap(map: Record<string, string>): void | Promise<void>;
  subscribe(onChange: () => void): () => void;
}

/**
 * The `lastSyncedAt` marker seam (docs/08 §8.3), stored next to the local store
 * (web localStorage key; brain kv slot) and wiped with sign-out alongside it.
 */
export interface AliasSyncMarker {
  get(): Promise<string | null>;
  set(updatedAt: string): Promise<void>;
}

export interface AliasSyncOptions {
  /** `deriveAliasKey(seed)` — cached; never logged. */
  key: Uint8Array;
  rest: AliasSyncRest;
  store: AliasSyncStore;
  marker: AliasSyncMarker;
  /** Push debounce (docs/08 §8.3 "~2 s"). */
  debounceMs?: number;
}

const DEFAULT_DEBOUNCE_MS = 2000;

/**
 * Whole-map LWW sync driver. Lifecycle:
 *   - `start()` — wire the local-change subscription, then pull once.
 *   - `resume()` — host reconnect / online / engine-start hook: pull, and retry
 *     a previously-failed push. No unbounded retry loop lives here.
 *   - `notifyLocalChange()` — schedule a debounced push (the brain calls this).
 *   - `stop()` — cancel timers and unsubscribe (sign-out / teardown).
 */
export class AliasSyncDriver {
  private started = false;
  private disposed = false;
  /** True while applying a pulled map, so the local-change subscription that
   *  `replaceMap`'s notify triggers does not bounce it straight back as a push. */
  private applyingRemote = false;
  /** A push failed and is awaiting the next mutation / resume (no timer loop). */
  private pendingPush = false;
  private pushTimer: ReturnType<typeof setTimeout> | null = null;
  private unsubscribe: (() => void) | null = null;
  private readonly debounceMs: number;

  constructor(private readonly opts: AliasSyncOptions) {
    this.debounceMs = opts.debounceMs ?? DEFAULT_DEBOUNCE_MS;
  }

  start(): void {
    if (this.started || this.disposed) return;
    this.started = true;
    this.unsubscribe = this.opts.store.subscribe(() => {
      if (this.applyingRemote) return;
      this.schedulePush();
    });
    void this.pull();
  }

  stop(): void {
    this.disposed = true;
    if (this.pushTimer !== null) {
      clearTimeout(this.pushTimer);
      this.pushTimer = null;
    }
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  /** Reconnect / online / engine-start: reconcile from the server, and retry a
   *  push that a previous connectivity gap left pending (docs/08 §8.3). */
  resume(): void {
    if (this.disposed) return;
    void this.pull();
    if (this.pendingPush) this.schedulePush();
  }

  /** Explicit local-mutation notice — the brain's mutation points call this
   *  (web drives it through the store subscription instead). */
  notifyLocalChange(): void {
    if (this.disposed || this.applyingRemote) return;
    this.schedulePush();
  }

  private schedulePush(): void {
    if (this.disposed) return;
    if (this.pushTimer !== null) clearTimeout(this.pushTimer);
    this.pushTimer = setTimeout(() => {
      this.pushTimer = null;
      void this.push();
    }, this.debounceMs);
  }

  /** GET the blob; if `updatedAt` differs from the marker, decrypt and REPLACE
   *  the local map wholesale, then advance the marker. A blob that fails to
   *  decrypt/parse is ignored (no contents logged) — this device's next push
   *  overwrites it (docs/08 §8.3). */
  private async pull(): Promise<void> {
    if (this.disposed) return;
    let response: AliasesBlobResponse;
    try {
      response = await this.opts.rest.getAliasesBlob();
    } catch {
      return; // offline / transient — a later resume retries
    }
    if (this.disposed) return;
    const { blob, updatedAt } = response;
    // Never uploaded (both null) — nothing to pull; local wins on the next push.
    if (updatedAt === null || blob === null) return;
    const marker = await this.opts.marker.get().catch(() => null);
    if (this.disposed || marker === updatedAt) return; // already in sync

    const map = decryptAliases(this.opts.key, fromBase64(blob));
    if (map === null) {
      // Corrupt / undecryptable: ignore silently, converge by overwriting.
      this.schedulePush();
      return;
    }
    this.applyingRemote = true;
    try {
      await this.opts.store.replaceMap(map);
    } finally {
      this.applyingRemote = false;
    }
    await this.opts.marker.set(updatedAt).catch(() => undefined);
  }

  /** Encrypt the full current map and PUT; store the returned `updatedAt` as the
   *  marker. On failure, flag a pending push (retried on the next mutation /
   *  resume) rather than looping. */
  private async push(): Promise<void> {
    if (this.disposed) return;
    const blob = toBase64(encryptAliases(this.opts.key, this.opts.store.getMap()));
    try {
      const { updatedAt } = await this.opts.rest.putAliasesBlob(blob);
      if (this.disposed) return;
      await this.opts.marker.set(updatedAt).catch(() => undefined);
      this.pendingPush = false;
    } catch {
      this.pendingPush = true; // retry on next mutation / resume — no retry loop
    }
  }
}
